import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

export type FdkInputType = 'email' | 'password' | 'search' | 'tel' | 'text' | 'url';

let nextInputId = 0;

@Component({
  selector: 'fdk-input',
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FdkInputComponent),
      multi: true,
    },
  ],
})
export class FdkInputComponent implements ControlValueAccessor {
  @Input({ required: true }) label!: string;
  @Input({ transform: booleanAttribute }) hideLabel = false;
  @Input() placeholder: string = '';
  @Input() value: string = '';
  @Output() readonly valueChange = new EventEmitter<string>();
  @Input() type: FdkInputType = 'text';
  @Input() autocomplete: string | null = null;
  @Input() list: string | null = null;
  @Input() name: string | null = null;
  @Input() minlength: number | null = null;
  @Input() maxlength: number | null = null;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) required = false;
  @Input({ transform: booleanAttribute }) error = false;
  @Input() errorMessage = '';

  readonly inputId = `fdk-input-${nextInputId++}`;
  readonly errorMessageId = `${this.inputId}-error`;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  get showErrorMessage(): boolean {
    return this.error && this.errorMessage.trim().length > 0;
  }

  get describedBy(): string | null {
    return this.showErrorMessage ? this.errorMessageId : null;
  }

  handleInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value = value;
    this.valueChange.emit(value);
    this.onChange(value);
  }

  handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: string | null | undefined): void {
    this.value = value ?? '';
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(onChange: (value: string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetectorRef.markForCheck();
  }
}
